import React from "react";
import { Radio, MapPin, CalendarClock, ExternalLink } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { extractYouTubeId } from "../lib/video";
import { useReveal } from "../hooks/useReveal";

export default function LiveSection() {
  const settings = useSettings();
  const { ref, inView } = useReveal();
  const live = settings?.live || {};
  const id = extractYouTubeId(live.videoId || "");
  const isLive = !!live.enabled && !!id;

  const embed = isLive
    ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&autoplay=0`
    : null;

  return (
    <section id="live" className="relative py-24 bg-[#0d0d0f]">
      <div className="max-w-7xl mx-auto px-5">
        <div ref={ref} className={`reveal ${inView ? "in-view" : ""} flex items-center gap-3 mb-4`}>
          <span className="relative flex h-3 w-3">
            {isLive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />}
            <span className={`relative inline-flex rounded-full h-3 w-3 ${isLive ? "bg-red-500" : "bg-white/30"}`} />
          </span>
          <span className="section-label text-gold text-xs">{isLive ? "Live Now" : "Live Stream"}</span>
        </div>

        <h2 className="display-font uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
          Watch Atlético <span className="text-gold">live.</span>
        </h2>

        <div className="mt-12 grid lg:grid-cols-[1.6fr_1fr] gap-8 items-start">
          <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black aspect-video">
            {isLive ? (
              <iframe
                title="live-stream"
                src={embed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <Radio className="text-white/25" size={54} />
                <p className="cond-font uppercase tracking-widest text-white/60 mt-4">We're not live right now</p>
                <p className="text-white/40 font-light mt-2 max-w-sm">
                  Check back on match day — the live stream will appear here automatically.
                </p>
              </div>
            )}
          </div>

          <div className="bg-card border border-white/10 rounded-xl p-7">
            <span className={`inline-flex items-center gap-2 cond-font uppercase tracking-widest text-xs px-3 py-1 rounded-full ${isLive ? "bg-red-500/15 text-red-400 border border-red-500/40" : "bg-white/5 text-white/50 border border-white/10"}`}>
              <span className={`h-2 w-2 rounded-full ${isLive ? "bg-red-500" : "bg-white/40"}`} />
              {isLive ? "On Air" : "Offline"}
            </span>
            <h3 className="cond-font uppercase tracking-wide text-2xl text-white mt-5">
              {live.title || "Atlético South Florida"}
            </h3>
            {live.location ? (
              <div className="flex items-center gap-2 text-white/65 font-light mt-4">
                <MapPin size={18} className="text-gold" /> {live.location}
              </div>
            ) : null}
            {live.scheduledText ? (
              <div className="flex items-center gap-2 text-white/65 font-light mt-3">
                <CalendarClock size={18} className="text-gold" /> {live.scheduledText}
              </div>
            ) : null}
            {isLive && (
              <a
                href={`https://www.youtube.com/watch?v=${id}`}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-2 mt-6 bg-gold text-black cond-font uppercase tracking-widest text-sm font-semibold px-5 py-3 rounded-sm hover:bg-gold-light transition-colors"
              >
                Open on YouTube <ExternalLink size={15} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
            )}
            <p className="text-white/35 text-xs font-light mt-6">
              Managed from the club admin — the stream location updates here in real time.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
